<template>
    <v-container class="mt-6">
        <v-row>
            <v-col>
                <h1>{{metaData.header}}</h1>
            </v-col>
        </v-row>
        <v-row class="my-6">
            <v-col class="text-center">
                <span class="text-md-body-1  font-weight-normal text--grey darken-2">{{ stepMessage }}</span>
            </v-col>
        </v-row>
        <v-row>
            <v-col>
                <v-stepper v-model="step" class="no-shadow transparent">
                    <v-stepper-header class="no-shadow px-md-16">
                        <v-stepper-step
                            :complete="step > 1"
                            step="1"
                            color="main--yellow "
                        >
                            <div>
                                {{$t('mainData')}}
                            </div>
                        </v-stepper-step>
                        <v-divider></v-divider>
                        <v-stepper-step
                            :complete="step > 2"
                            step="2"
                            color="main--yellow "
                        >
                            <div>
                                {{$t('typesDrugsByPhase')}}
                            </div>
                        </v-stepper-step>
                        <v-divider></v-divider>
                        <v-stepper-step
                            :complete="step > 3"
                            step="3"
                            color="main--yellow"
                        >
                            <div>
                                {{$t('problemsAndBudget')}}
                            </div>
                        </v-stepper-step>
                        <v-divider></v-divider>
                        <v-stepper-step
                            step="4"
                            color="main--yellow"
                        >
                            <div>
                                {{$t('calculationResult')}}
                            </div>
                        </v-stepper-step>
                    </v-stepper-header>
                    <v-stepper-items class="no-shadow">
                        <v-stepper-content step="1" class="px-1">
                            <main-data
                                :step="step"
                                :cultures="cultures"
                                @next-step="toNextStep"
                            ></main-data>
                        </v-stepper-content>
                        <v-stepper-content step="2" class="px-1">
                            <categories
                                :step="step"
                                :categories="categories"
                                :cultures="cultures"
                                @next-step="onCategoriesStepValidated"
                                @previous-step="back"
                            ></categories>
                        </v-stepper-content>

                        <v-stepper-content step="3" class="px-1">
                            <attributes-budget
                                :step="step"
                                :categories="categories"
                                :cultures="cultures"
                                :attributes="attributes"
                                @next-step="onAttributesBudgetValidated"
                                @previous-step="back"
                            ></attributes-budget>
                        </v-stepper-content>

                        <v-stepper-content step="4" class="px-1">
                            <results
                                :step="step"
                                :results="results"
                                :cultures="cultures"
                                :categories="categories"
                                :attributes="attributes"

                                @recalculate="recalculateResult"
                                @export-to-xlsx="exportToXlsx"
                                @shareLink="shareLink"
                                @to-first-step="toFirstStep"

                            ></results>
                        </v-stepper-content>
                    </v-stepper-items>
                </v-stepper>
            </v-col>
        </v-row>
        <loading-dialog
            :is-open="openLoadingDialog"
        ></loading-dialog>
        <v-snackbar
            v-model="snackOpen"
            centered
        >
            {{ snackText}}
            <template v-slot:action="{ attrs }">
                <v-btn
                    text
                    icon
                    v-bind="attrs"
                    @click="closeSnack"
                >
                    <v-icon color="grey">close</v-icon>
                </v-btn>
            </template>
        </v-snackbar>
    </v-container>
</template>

<script>
import MainData from "./main-data-step/MainDataStep";
import Categories from "./categories-step/CategoriesStep";
import AttributesBudget from "./attributes-budget-step/AttributesBudgetStep";
import Results from "./results-step/ResultsStep";
import LoadingDialog from "../../../app-front/components/dialogs/LoadingDialog";

import AppSnack from "../../../general/mixins/AppSnack";
import CalculatorDataProvider from "../../../app-front/mixins/CalculatorDataProvider";

import CalculatorService from "../../../app-front/services/api/CalculatorService";
import "./calculator.scss"

export default {
    name: "Calculator",
    components: {
        MainData,
        Categories,
        AttributesBudget,
        Results,
        LoadingDialog
    },
    mixins: [ AppSnack, CalculatorDataProvider ],
    props: [ 'id' ],
    data() {
        return {
            metaUrl: '/calculate',
            step: 1,
            attributes: [],
            categories: [],
            cultures: [],
            results: [],
            hasInitial: false,
            searchID: null,
            openLoadingDialog: false
        }
    },
    computed: {
        stepMessage() {
            switch(this.step) {
                case 1: return this.$t('calculatorStep1stHeader');
                case 2: return this.$t('calculatorStep2ndHeader');
                case 3: return this.$t('calculatorStep3rdHeader');
                case 4: return this.$t('calculatorStep4thHeader');
                default: return false;
            }
        },
    },
    methods: {
        //steps
        back() {
            this.step--;
        },
        toNextStep(){
            this.step++;
        },
        toFirstStep() {
            this.step = 1;
        },
        onCategoriesStepValidated() {
            this.setAttributesByCategoryID()
                .then(res => {
                    this.attributes = res.data;
                })
                .catch(err => {
                    console.log(err);
                }).finally(() => {
                    this.step++
            })
        },
        onAttributesBudgetValidated() {
            const params = {
                searchData: this.calculatorFullData,
                history: true
            }
            this.setResults(params)
        },
        //Data
        setAttributesByCategoryID() {
            const categoryIDs = this.categoryGroupedIDs
            CalculatorService.setLocale(this.$store.state.language.appLanguage);
            return CalculatorService.getAttributes({
                categoryIDs: categoryIDs
            });
        },
        setCultures() {
            CalculatorService.setLocale(this.$store.state.language.appLanguage);
            CalculatorService.getCultures()
                .then(res=>{
                    this.cultures = res.data
                })
                .catch(err=>{
                    console.log(err)
                })
        },
        setCategories() {
            CalculatorService.setLocale(this.$store.state.language.appLanguage);
            CalculatorService.getCategories()
                .then(res => {
                    this.categories = res.data;
                })
                .catch(err => {
                    console.log(err);
                });
        },
        setInitial() {
            CalculatorService.setLocale(this.$store.state.language.appLanguage);
            CalculatorService.getInitial(this.id)
                .then(res => {
                    this.setCalculatorFullData(res.data);
                    this.setAttributesByCategoryID()
                        .then(res => {
                            this.attributes = res.data;
                        })
                        .catch(err => {
                            console.log(err);
                        });

                    const params = {
                        searchData: this.calculatorFullData,
                        history: false
                    }
                    this.setResults(params)
                })
                .catch(err => {
                    console.log(err);
                });
        },
        setResults(params) {
            this.openLoadingDialog = true;

            CalculatorService.setLocale(this.$store.state.language.appLanguage);
            CalculatorService.getResults(params).then(res => {
                this.results = res.data.products
                if(res.data.searchID) this.searchID = res.data.searchID

                this.step = 4;
            }).catch(err => {
                console.log(err)
            }).finally(() => {
                this.openLoadingDialog = false;
            });
        },

        //other
        exportToXlsx(data) {
            CalculatorService.setLocale(this.$store.state.language.appLanguage);
            CalculatorService.getXlsFile({products: data})
                .then((res) => {
                    const url = window.URL.createObjectURL(new Blob([res.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', 'file.xlsx');
                    document.body.appendChild(link);
                    link.click();
                })
                .catch(err=>{
                    console.log(err);
                    this.snackText = this.$t('selectDrugs');
                    this.openSnack();
                })
        },
        recalculateResult() {
            this.onAttributesBudgetValidated()
        },
        copyToClipboard(str) {
            const el = document.createElement('textarea');
            el.value = str;
            document.body.appendChild(el);
            el.select();
            document.execCommand('copy');
            document.body.removeChild(el);
        },
        shareLink() {
            if (this.searchID !== null) {
                const prefix = this.$store.getters.language.prefixes[this.$store.getters.language.appLanguage];
                const searchResultsURL = `${window.location.origin}${prefix}calculator/${this.searchID}`;
                this.copyToClipboard(searchResultsURL)
                this.snackText = this.$t('linkCopied')
                this.openSnack();
            } else {
                this.snackText = this.$t('linkNotCopied')
                this.openSnack();
            }
        },
    },
    created() {
        if (window.fbq) window.fbq('track', 'Calculator');
        this.resetCalculator();
        this.setCultures();
        this.setCategories();
        if(this.id){
            this.setInitial();
        } else {
            this.setDefaultItem();
        }
    },
    watch: {
        step() {
            window.scrollTo(0, 0);
        },
    }
}
</script>

<style scoped>

</style>
